<script lang="ts">
/** Body scroll lock shared by every open drawer (one closing must not unlock another that just opened). */
let locks = 0;
function lockScroll(on: boolean): void {
  locks = Math.max(0, locks + (on ? 1 : -1));
  document.body.style.overflow = locks ? 'hidden' : '';
}
</script>

<script setup lang="ts">
/**
 * The context drawer: a panel that slides in from the right for creating and editing entities without
 * leaving the list. Modal (focus is trapped, Esc and the backdrop close it, focus returns to the opener),
 * teleported to <body> so no parent `overflow` or stacking context can clip it. Full-width on phones.
 *
 * Pass `dirty` while the form has unsaved edits; closing then asks first (same wording as the editors).
 *
 * Slots: default (scrolling body), `footer` (sticky actions), `header-extra` (under the title).
 */
import { confirmDialog } from '../stores/confirm';
import { useI18n } from 'vue-i18n';
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    description?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    /** Block closing (e.g. while a request is in flight). */
    persistent?: boolean;
    /** Unsaved edits: Esc, the backdrop and ✕ ask before discarding them (gap analysis §14 #3). */
    dirty?: boolean;
  }>(),
  { size: 'md', description: undefined, persistent: false, dirty: false },
);
const emit = defineEmits<{ close: [] }>();

const panel = ref<HTMLElement | null>(null);
const titleId = useId();
const descId = useId();
const width = computed(
  () => ({ sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-xl', xl: 'sm:max-w-5xl' })[props.size],
);
let opener: HTMLElement | null = null;
let locked = false;

async function close(): Promise<void> {
  if (props.persistent) return;
  if (props.dirty && !await confirmDialog(t('ui.discardChanges'))) return;
  emit('close');
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.stopPropagation();
    close();
    return;
  }
  if (e.key !== 'Tab' || !panel.value) return;
  const items = [...panel.value.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
  if (!items.length) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      opener = document.activeElement as HTMLElement | null;
      document.addEventListener('keydown', onKey);
      lockScroll(true);
      locked = true;
      await nextTick();
      // First field if there is one, else the panel itself (so screen readers announce the dialog).
      (panel.value?.querySelector<HTMLElement>('[autofocus],input,select,textarea') ?? panel.value)?.focus();
    } else {
      document.removeEventListener('keydown', onKey);
      if (locked) lockScroll(false);
      locked = false;
      // Only give focus back if nothing else (e.g. another drawer) has taken it.
      if (
        !document.activeElement ||
        document.activeElement === document.body ||
        panel.value?.contains(document.activeElement)
      )
        opener?.focus?.();
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey);
  if (locked) lockScroll(false);
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-[2px]"
        aria-hidden="true"
        @click="close"
      />
    </Transition>
    <Transition
      enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
      enter-from-class="translate-x-full rtl:-translate-x-full"
      leave-active-class="transition-transform duration-200 ease-in motion-reduce:transition-none"
      leave-to-class="translate-x-full rtl:-translate-x-full"
    >
      <section
        v-if="open"
        ref="panel"
        class="fixed inset-y-0 end-0 z-50 flex w-full flex-col bg-white shadow-2xl outline-none dark:bg-slate-900"
        :class="width"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="description ? descId : undefined"
        tabindex="-1"
      >
        <header class="flex items-start gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div class="min-w-0 flex-1">
            <h2 :id="titleId" class="truncate text-base font-semibold">{{ title }}</h2>
            <p v-if="description" :id="descId" class="mt-0.5 text-sm text-slate-500">{{ description }}</p>
            <slot name="header-extra" />
          </div>
          <button
            type="button"
            class="-me-1 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40 dark:hover:bg-slate-800 dark:hover:text-white"
            :aria-label="t('ui.closePanel')"
            :disabled="persistent"
            @click="close"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-5 w-5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              aria-hidden="true"
            >
              <path d="M5 5l10 10M15 5L5 15" stroke-linecap="round" />
            </svg>
          </button>
        </header>
        <div class="flex-1 overflow-y-auto px-5 py-5">
          <slot />
        </div>
        <footer
          v-if="$slots.footer"
          class="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-800 dark:bg-slate-900/80"
        >
          <slot name="footer" />
        </footer>
      </section>
    </Transition>
  </Teleport>
</template>
